import { useIdToken, useUpdateProfile } from 'react-firebase-hooks/auth';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { auth as firebaseAuth } from '@/app/providers/firebase';
import { setUserInfo } from '@/features/auth/store/auth';
import { useUpdateUserMutation } from '@/features/chat/services/user-update';
import { useAppDispatch } from '@/hooks/store';
import { Button, Form, Input, toast } from '@/uikit/components';

import styles from './AccountDetail.module.scss';

const schema = z.object({
  name: z
    .string()
    .min(1, "Password can't be empty.")
    .min(3, 'Must be at least 6 characters')
    .optional(),
});

interface AccountDetailProps {
  onSubmit: () => void;
}
export const AccountDetail = ({
  onSubmit,
}: AccountDetailProps): React.ReactNode => {
  const [user] = useIdToken(firebaseAuth);
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [updateProfile, updating] = useUpdateProfile(firebaseAuth);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const handleSubmit = async (event: { name?: string }): Promise<void> => {
    const displayName = event.name;
    if (user?.email !== null) {
      try {
        await updateUser({
          email: user?.email,
          fullName: event.name,
          userId: user?.uid,
        }).unwrap();
        dispatch(
          setUserInfo({
            uid: user?.uid ?? '',
            fullName: event.name,
          }),
        );

        await updateProfile({ displayName });

        onSubmit();
      } catch (error) {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };
  return (
    <div className={styles.container}>
      <Form schema={schema} onSubmit={handleSubmit} mode="onChange">
        <div className={styles.container__form}>
          <Form.Item name="name">
            <Input
              type="text"
              label={t('account.profile.fullName')}
              name="name"
              placeholder={t('account.profile.fullNamePlaceholder')}
            />
          </Form.Item>
          <Button
            size="xl"
            type="submit"
            className={styles.container__button}
            loading={isLoading || updating}
          >
            <Trans i18nKey="userUpdate.saveAndContinue">Save & Continue</Trans>
          </Button>
        </div>
      </Form>
    </div>
  );
};
